import { Component, computed, effect, inject, model, OnInit, signal } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { FacetConfigTimelineSettings, SearchTab, TimelineInterval } from '@aipb/gulliver/catalog/model';
import { CastorService } from '@aipb/gulliver/castor/data-access';
import { TimelineFilterBarChartComponent, TimelineRange } from '../timeline-bar-chart/timeline-bar-chart.component';
import { MatSliderModule } from '@angular/material/slider';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule } from '@jsverse/transloco';
import { FacetRepository, FacetService, SearchService } from '@aipb/gulliver/catalog/data-access';
import { MtxButtonModule } from '@ng-matero/extensions/button';
import { Observable } from 'rxjs';
import { differenceInDays, sub } from 'date-fns';
import { FacetBaseComponent } from '../facet-base.component';
import { processTimelineRangeRules, trimZeroTimelineItems } from '../../facet.util';

interface TimelineItem {
  label_from: string;
  label_to: string;
  value: number;
  value_in_percent: number;
}

@Component({
  selector: 'cat-facet-timeline',
  standalone: true,
  imports: [
    CommonModule,
    TimelineFilterBarChartComponent,
    MatSliderModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    TranslocoModule,
    MtxButtonModule
  ],
  templateUrl: './facet-timeline.component.html',
  styleUrl: './facet-timeline.component.scss'
})
export class FacetTimelineComponent extends FacetBaseComponent<FacetConfigTimelineSettings> implements OnInit {
  items = signal<TimelineItem[]>([]);
  start = model<number>(0);
  end = model<number>(0);
  beforeCount = signal(0);
  afterCount = signal(0);
  rangeStart = signal<Date>(null);
  rangeEnd = signal<Date>(null);
  range = computed<TimelineRange>(() => {
    const start = this.start();
    const end = this.end();

    return {
      start,
      end
    };
  });
  startDate = model<Date>(
    sub(new Date(), {
      months: 3
    })
  );
  endDate = model<Date>(new Date());
  intervalValue = model<number>(1);
  intervalName = model<TimelineInterval>('DAY');
  rangeLabel = computed(() => {
    const items = this.items();
    const range = this.range();
    const rangeEnd = this.rangeEnd();

    if (items.length === 0) return '';

    const start = items[range.start];
    const end = items[range.end];

    const startDate = new Date(start.label_from);
    let endDate = new Date(end.label_to);

    if (range.start === range.end && items.length === 1) {
      endDate = rangeEnd;
    }

    return `${formatDate(startDate, 'dd.MM.yyyy', 'cs')} - ${formatDate(endDate, 'dd.MM.yyyy', 'cs')}`;
  });
  intervals: TimelineInterval[] = ['DAY', 'MONTH', 'YEAR'];
  private facetRepository = inject(FacetRepository);
  loading$: Observable<boolean> = this.facetRepository.loading$;
  private castor = inject(CastorService);
  private searchService = inject(SearchService);
  private facetService = inject(FacetService);

  constructor() {
    super();

    effect(
      () => {
        const startDate = this.startDate();
        const endDate = this.endDate();
        const rules = this.settings.rules;

        if (!startDate || !endDate || !rules) return;

        const duration = differenceInDays(endDate, startDate);
        const rule = processTimelineRangeRules(duration, rules);

        if (rule === null) return;

        this.intervalValue.set(rule.value);
        this.intervalName.set(rule.interval);
      },
      {
        allowSignalWrites: true
      }
    );

    effect(() => {
      const items = this.items();
      const range = this.range();
      const rangeEnd = this.rangeEnd();

      if (items.length === 0) return;

      const start = items[range.start];
      const end = items[range.end];

      const startDate = new Date(start.label_from);
      let endDate = new Date(end.label_to);

      if (range.start === range.end && items.length === 1) {
        endDate = rangeEnd;
      }

      endDate.setUTCHours(23, 59, 59);

      this.searchService.updateTimelineResultCount(
        this.searchItem,
        this.facetService.getFacetTimelineQuery(this.searchItem.id, this.settings.fieldId, startDate, endDate)
      );
    });

    effect(() => {
      const startDate = this.startDate();
      const endDate = this.endDate();

      if (!startDate || !endDate) return;

      const intervalValue = this.intervalValue();
      const intervalName = this.intervalName();

      this.castor
        .rangeFacet({
          fieldId: this.settings.fieldId,
          coreId: this.searchItem.coreId,
          start: startDate.toISOString(),
          end: endDate.toISOString(),
          gap: `+${intervalValue}${intervalName}`,
          query: this.searchItem.search.q,
          otherAll: true
        })
        .subscribe((data) => {
          const max = Object.keys(data.counts).reduce((a, b) => Math.max(a, data.counts[b]), -Infinity);
          this.rangeStart.set(new Date(data.start));
          this.rangeEnd.set(new Date(data.end));
          this.beforeCount.set(data.before);
          this.afterCount.set(data.after);

          const items: TimelineItem[] = [];

          for (let i = 0; i < Object.keys(data.counts).length; i++) {
            const key = Object.keys(data.counts)[i];
            let label_to = '';

            if (i + 1 < Object.keys(data.counts).length) {
              label_to = Object.keys(data.counts)[i + 1];
            } else {
              label_to = new Date(data.end).toISOString();
            }

            items.push({
              label_from: key,
              label_to: label_to,
              value: data.counts[key],
              value_in_percent: data.counts[key] !== 0 ? Math.floor((data.counts[key] / max) * 100) : 0
            });
          }

          this.items.set(trimZeroTimelineItems([...items]));
          this.start.set(0);
          this.end.set(this.items().length - 1);
        });
    });
  }

  ngOnInit() {
    if (this.searchItem && this.searchItem.timelineRange) {
      this.startDate.set(new Date(this.searchItem.timelineRange[0]));
      this.endDate.set(new Date(this.searchItem.timelineRange[1]));
    }
  }

  onSearch() {
    const items = this.items();
    const range = this.range();
    const rangeEnd = this.rangeEnd();

    if (items.length === 0) return;

    const start = items[range.start];
    const end = items[range.end];

    const startDate = new Date(start.label_from);
    let endDate = new Date(end.label_to);

    if (range.start === range.end && items.length === 1) {
      endDate = rangeEnd;
    }

    endDate.setUTCHours(23, 59, 59);

    const query = this.facetService.getFacetTimelineQuery(
      this.searchItem.id,
      this.settings.fieldId,
      startDate,
      endDate
    );

    const searchTab: SearchTab = {
      solrSearchParams: {
        query: query,
        coreId: this.searchItem.coreId,
        sort: this.searchItem.search_sort
      },
      settings: {},
      timelineRange: [startDate.toISOString(), endDate.toISOString()]
    };

    this.searchService.addTab(searchTab, true);
  }
}
